import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
// Type-only: the `/urql` subpath is the real codegen output, which the unit
// suite's stub does not cover and CI does not generate for it.
import type { SolutionTypeEnum } from '@pypestream/api-services/urql';
import { throwErrorWithId } from '@pypestream/utils';

import { checkForErrors } from '../../utils';
import {
  ProStudioContext,
  ProStudioEvents,
  SelectedSolution,
} from '../prostudio.xstate.types';

/**
 * Main <-> survey. The type is the `Solution.type` column, written by
 * `updateSolution` (backend #1417), and it is what every load reads
 * (`readBotType`). So it is written here for every solution, a migrated one
 * included: Bot Manager's PatchBotById answers 404 on main, and even where it
 * worked it would leave the column at its `main` default, so the next load
 * undid the switch while the toast said it had landed (PSTUDIO-4354).
 */
export const changeBotType = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<SelectedSolution | void> => {
  if (event.type !== 'setBotType') {
    return;
  }

  const { selectedBot } = ctx;
  const { botType } = event;

  const solutionId = selectedBot?.uniqueId;
  if (!selectedBot || !solutionId) {
    throwErrorWithId('Solution ID is required');
    return;
  }

  if (!botType) {
    throwErrorWithId('Bot type is required');
    return;
  }

  const response = await urqlGql.updateSolutionType({
    solutionId,
    // The enum's values are exactly 'main' | 'survey'.
    type: botType as SolutionTypeEnum,
  });
  checkForErrors(response);

  const type = response.data?.admin_?.updateSolution?.type;
  if (type !== 'main' && type !== 'survey') {
    throwErrorWithId('Updated solution type is required');
    return;
  }

  return { ...selectedBot, botType: type } as SelectedSolution;
};
